import { shouldShowHonorific } from './genderDetection';

/**
 * The single source of truth for turning a contact's raw fields into the shape
 * a contact card renders (`ContactCardBodyContact` in the dashboard).
 *
 * Every card surface — search, cadence/campaign, contacts, leads, recent — used
 * to hand-roll this mapping, and they drifted: the /contacts page hardcoded
 * `displayHonorific: false` and `liOpenLink: null`, and each server transformer
 * recomputed the honorific gate separately. Route all of them through
 * `buildContactCardContact` so the field mapping (and especially the honorific
 * gating) can never diverge again.
 */

export interface ContactCardTriggeredSignal {
  bucketKey: string;
  bucketName: string;
  reason: string | null;
}

export interface ContactCardContactInput {
  id: string;

  firstName: string | null | undefined;
  lastName: string | null | undefined;
  photoUrl: string | null | undefined;

  phone: string | null | undefined;
  phoneVerified: boolean | null | undefined;

  professionalEmail: string | null | undefined;
  professionalEmailVerified: boolean | null | undefined;

  title: string | null | undefined;
  stageId: string | null | undefined;

  honorificTitle: string | null | undefined;
  liOpenLink: boolean | null | undefined;

  triggeredSignals?: ContactCardTriggeredSignal[] | null;
  signalsComputed?: boolean | null;

  /**
   * Honorific gating inputs. When provided, `displayHonorific` is computed
   * centrally via `shouldShowHonorific` — the single source of truth. Prefer
   * threading these down over precomputing the flag yourself.
   */
  minAge?: number | null;
  honorificAgeThreshold?: number | null;

  /**
   * Escape hatch for callers whose upstream layer already gated the honorific
   * with the SAME `shouldShowHonorific` logic (the SvrContact transformer
   * computes it from the team threshold + age estimate before this point).
   * Takes precedence over `minAge`/`honorificAgeThreshold` when set.
   *
   * NEVER pass a hardcoded literal here — hardcoding `false` is exactly the
   * /contacts bug this helper exists to eliminate. Pass raw age inputs instead.
   */
  displayHonorific?: boolean;
}

export interface ContactCardContact {
  id: string;

  firstName: string | null;
  lastName: string | null;
  photoUrl: string | null;

  phone: string | null;
  phoneVerified: boolean | null;

  professionalEmail: string | null;
  professionalEmailVerified: boolean | null;

  title: string | undefined;
  stageId: string | undefined;

  honorificTitle: string | null;
  displayHonorific: boolean;
  liOpenLink: boolean | null;

  triggeredSignals: ContactCardTriggeredSignal[] | null;
  signalsComputed: boolean;
}

export function buildContactCardContact(input: ContactCardContactInput): ContactCardContact {
  const displayHonorific =
    input.displayHonorific ?? shouldShowHonorific(input.honorificTitle, input.minAge, input.honorificAgeThreshold);

  return {
    id: input.id,
    firstName: input.firstName ?? null,
    lastName: input.lastName ?? null,
    photoUrl: input.photoUrl ?? null,
    phone: input.phone ?? null,
    phoneVerified: input.phoneVerified ?? null,
    professionalEmail: input.professionalEmail ?? null,
    professionalEmailVerified: input.professionalEmailVerified ?? null,
    title: input.title ?? undefined,
    stageId: input.stageId ?? undefined,
    honorificTitle: input.honorificTitle ?? null,
    displayHonorific,
    liOpenLink: input.liOpenLink ?? null,
    triggeredSignals: input.triggeredSignals ?? null,
    signalsComputed: input.signalsComputed ?? false,
  };
}
